/** @jsx jsx */
import { jsx } from '@emotion/react';
import React, { useMemo } from 'react';
import { DonutGraph } from './DonutGraph';
import { NonIdealState } from '@blueprintjs/core';
import { selectDilDefinitionByName } from 'srs-middleware';
import styled from '@emotion/styled';
import { DefaultChartColorScales } from 'styles/chartColors';
import { NO_DATA_MESSAGE } from 'widgets/chartUtils';
//import { getColorScale } from 'components/colorUtils';

/**
 * ChartWrapper for the Donut Graph.
 *
 * @param getColorScale
 * @param dimensions
 * @param data
 * @param selectedDefinitionName
 * @param endpointQuery
 * @param widgetConfiguration
 * @returns {*}
 * @constructor
 */
export function DonutGraphWrapper({
	getColorScale,
	dimensions,
	data,
	noDataMessage=NO_DATA_MESSAGE,
	selectedDefinitionName,
	endpointQuery,
	widgetConfiguration,
}) {
	// get the actual definition
	const selectedDefinition = useMemo(() => {
		return selectDilDefinitionByName(selectedDefinitionName);
	}, [selectedDefinitionName]);

	const convertedData = useMemo(() => {
		if (!data?.entries || !selectedDefinition || !endpointQuery?.options?.propertyList) return;

		const items = data.entries || [];

		// empty data is "valid"
		if (items.length === 0) return [];

		const converted = items.map((entry) => {
			return {
				key: entry.key,
				entries: entry.children.map(({ key, count, value }) => {
					return {
						name: key,
						value: value,
						reference: count,
					};
				}),
			};
		});

		if (widgetConfiguration?.colorScale === DefaultChartColorScales.Sequential) {
			getColorScale.domain([0, data.keys[1].length || 0]);
			converted.forEach((entry) => {
				entry.entries.forEach((d) => {
					d.colorValue = data.keys[1].indexOf(d.name);
				});
			});
		} else {
			getColorScale.domain(data.keys[1]);
			converted.forEach((entry) => {
				entry.entries.forEach((d) => {
					d.colorValue = d.name;
				});
			});
		}

		// top level will turn into an array of charts to show
		return converted;
	}, [data.entries, data.keys, selectedDefinition, endpointQuery, widgetConfiguration, getColorScale]);

	// If the state is not ideal (the data is null or empty) render the nonIdealState message
	if (!convertedData || convertedData.length <= 0)
		return (
			<NonIdealState
				title="No Data Found"
				icon="graph-remove"
				description={noDataMessage}
			/>
		);

	return (
		<StyledContainer count={convertedData.length}>
			{convertedData.map((entry) => {
				return (
					<DonutGraph
						key={entry.key}
						data={entry.entries}
						centerLabel={entry.key}
						containerDimensions={roughDimensions(
							{ height: dimensions.height * 0.9, width: dimensions.width * 0.9 },
							convertedData.length
						)}
						widgetConfiguration={widgetConfiguration}
						getColor={getColorScale}
					/>
				);
			})}
		</StyledContainer>
	);
}

const roughDimensions = (dimensions, count) => {
	switch (count) {
		case 1:
			return dimensions;
		case 2:
			return { height: dimensions.height / 2, width: dimensions.width };
		case 3:
			return { height: dimensions.height / 3, width: dimensions.width };
		case 4:
			return { height: dimensions.height / 2, width: dimensions.width / 2 };
		case 5:
		case 6:
			return { height: dimensions.height / 3, width: dimensions.width / 2 };
		default:
			return dimensions;
	}
};

const StyledContainer = styled.div(({ count }) => ({
	flex: '1 1 auto',
	display: 'grid',
	gridTemplateColumns: `repeat(${sizes(count)[0]}, 1fr)`,
	gridTemplateRows: `repeat(${sizes(count)[1]}, 1fr)`,
	gridColumnGap: '20px',
	gridRowGap: '20px',
	overflow: 'hidden',
	padding: '20px',
}));

const sizes = (count) => {
	switch (count) {
		case 1:
			return [1, 1];
		case 2:
			return [1, 2];
		case 3:
			return [1, 3];
		case 4:
			return [2, 2];
		case 5:
		case 6:
			return [2, 3];
		default:
			return [1, 1];
	}
};
